'use client';

import { clsx } from 'clsx';
import { FC, ReactNode } from 'react';

import { Image } from '@repo/ui/Image';
import { Tabs } from '@/general/components/Tabs/Tabs';
import { Tab } from '@/general/components/Tabs/Tabs.type';
import { slugify } from '@/general/helpers/slugify';
import { CarouselTabCars } from '@/hygiene/components/CarouselTabCars/CarouselTabCars';
import { isNilOrEmpty } from '@/utils/isNilOrEmpty';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { FlexibleSection } from '../flexibleContent.type';
import { getSectionClasses } from '../getSectionClasses.helper';

interface Props extends FlexibleSection {
  sectionData: {
    carouselName: string;
    title?: string;
    tabs: {
      title: string;
      icon?: string;
      tiles: {
        id: string;
        tile: ReactNode;
      }[];
      footerBanner?: string;
      cta?: {
        buttonType: string;
        label: string;
        href: string;
      };
    }[];
  };
}

export const VehicleCarousel: FC<Props> = ({ sectionData, sectionBackground }) => {
  const tabs: Tab[] = sectionData.tabs.map((item, index) => {
    const id = `${slugify(item.title)}-${index}`;

    return {
      id,
      title: item.title,
      icon: item.icon ? (
        <Image
          className="absolute top-0 left-0 block h-full w-full object-contain"
          src={item.icon}
          alt=""
          width={48}
          height={24}
        />
      ) : undefined,
      content: (
        <CarouselTabCars
          carouselName={sectionData.carouselName}
          carouselValue={id}
          tiles={item.tiles}
          footerBanner={item.footerBanner}
          cta={
            item.cta?.buttonType === 'link'
              ? {
                  label: item.cta.label,
                  url: item.cta.href,
                }
              : undefined
          }
        />
      ),
      onClick: () => {
        pushToDataLayer({
          event: 'carousel_tab_change',
          carousel: sectionData.carouselName,
          tab: id,
        });
      },
    } as Tab;
  });

  return (
    <section
      className={clsx(getSectionClasses(sectionBackground), 'group/vehicle-carousel')}
      data-testid={`${slugify(sectionData.carouselName)}-carousel`}
    >
      {!isNilOrEmpty(sectionData.title) && (
        <header className="mb-6">
          <h2 className={clsx('font-extrabold', 'c-fs-h4')} data-testid={`${slugify(sectionData.carouselName)}-title`}>
            {sectionData.title}
          </h2>
        </header>
      )}
      {tabs.length === 1 ? (
        tabs[0].content
      ) : (
        <Tabs tabsId={sectionData.carouselName} tabs={tabs} variant="vehicle-carousel" />
      )}
    </section>
  );
};
